import { useId, useMemo } from 'react'

import type { CheckboxGroupProps } from './checkboxGroup.types'

import Box from '../box'
import { styled, vui } from '../core'
import {
  selectionControlGroupGap,
  selectionControlLabelSize,
  selectionControlStackGap,
} from '../selectionControl/theme'
import T from '../t'
import { cs, filterUndefined } from '../utils'
import { checkboxColors } from './consts'
import { CheckboxGroupProvider } from './context'

export const CheckboxGroupBase = styled.divBox`
  display: flex;
  flex-direction: column;
`

/**
 * Organizes layout and display of multiple checkboxes as a row or column.
 * Exposes some props to the children via context. `label`/`helpText` render an optional group
 * heading/description around the stack.
 */
export const CheckboxGroup = vui<'div', CheckboxGroupProps>((props, ref) => {
  const {
    children,
    className,
    defaultValue,
    disabled,
    gap,
    groupGap: groupGapProp,
    helpText,
    isRow,
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    size,
    variant,
    ...rest
  } = props

  const context = useMemo(
    () => filterUndefined({ defaultValue, disabled, name, onBlur, onChange, onFocus, size, variant }),
    [defaultValue, disabled, name, onBlur, onChange, onFocus, size, variant],
  )

  const groupSize = size ?? 'md'
  // `gap` (SystemProps) always controls checkbox-to-checkbox spacing; `groupGap` only applies to the
  // label/help-text wrapper below, so the two can't collide when both are set.
  const aliasedProps = filterUndefined({
    flexDirection: isRow ? 'row' : undefined,
    gap: gap ?? selectionControlStackGap[groupSize] ?? selectionControlStackGap.md,
  } as const)

  const generatedId = useId()
  const labelId = label ? `${generatedId}-label` : undefined
  const helpTextId = helpText ? `${generatedId}-help-text` : undefined

  if (!label && !helpText) {
    return (
      <CheckboxGroupProvider value={context}>
        <CheckboxGroupBase className={cs('vui-checkboxGroup', className)} ref={ref} {...aliasedProps} {...rest}>
          {children}
        </CheckboxGroupBase>
      </CheckboxGroupProvider>
    )
  }

  return (
    <CheckboxGroupProvider value={context}>
      <Box
        className={cs('vui-checkboxGroup', className)}
        column
        gap={groupGapProp ?? selectionControlGroupGap[groupSize] ?? selectionControlGroupGap.md}
        ref={ref}
        {...rest}
      >
        {label && (
          <T
            className="vui-checkboxGroupLabel"
            color={checkboxColors.label}
            fontWeight="demi"
            id={labelId}
            size={selectionControlLabelSize[groupSize] ?? selectionControlLabelSize.md}
          >
            {label}
          </T>
        )}
        <CheckboxGroupBase
          aria-describedby={helpTextId}
          aria-labelledby={labelId}
          className="vui-checkboxGroupStack"
          role="group"
          {...aliasedProps}
        >
          {children}
        </CheckboxGroupBase>
        {helpText && (
          <T className="vui-checkboxGroupHelpText" color={checkboxColors.helper} id={helpTextId} size="xs">
            {helpText}
          </T>
        )}
      </Box>
    </CheckboxGroupProvider>
  )
})

CheckboxGroup.displayName = 'CheckboxGroup'

export default CheckboxGroup
